import { useState, useCallback, useRef, useEffect } from 'react'
import { httpGetUserImpersonate } from '../http-requests/admin/users.http'
import { useDispatch } from 'react-redux'
import { showAlert } from '../store/appGenerics'
import { ALERT_LEVELS } from '../helpers/constants'

interface ImpersonationMessage {
    type: 'IMPERSONATE'
    token: string
    adminToken: string
    route?: string
}

interface ImpersonationResponse {
    type: 'IMPERSONATE_SUCCESS' | 'IMPERSONATE_ERROR'
    message?: string
}

interface UseImpersonateNavigateUserReturn {
    isLoading: boolean
    impersonateNavigateUser: (userId: string) => Promise<void>
}

const CLIENT_DOMAIN_MAP: Record<string, string> = {
    'mediabox.k-stage.dev': 'https://navigate.frontend.k-stage.dev',
    'mediabox.translated.com': 'https://navigate.translated.com',
    // enable this for local testing
    // 'localhost:56034': 'http://localhost:5173',
    // 'localhost': 'http://localhost:5173' 
    // 'localhost:3000': 'http://localhost:5174',
}
export const useImpersonateNavigateUser = (adminAccessToken: string): UseImpersonateNavigateUserReturn => {
    const [isLoading, setIsLoading] = useState(false)
    const dispatch = useDispatch()
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const listenerRef = useRef<((event: MessageEvent) => void) | null>(null)
    const windowRef = useRef<Window | null>(null)

    const getClientDomain = useCallback((): string => {
        const currentHost = window.location.hostname === 'localhost' 
            ? window.location.host 
            : window.location.hostname
        const clientDomain = CLIENT_DOMAIN_MAP[currentHost]
        
        if (!clientDomain) {
            throw new Error(`No client domain mapping found for ${currentHost}`)
        }
        
        return `${clientDomain}?is_impersonating=true`
    }, [])

    const isValidClientOrigin = useCallback((origin: string): boolean => {
        const validOrigins = Object.values(CLIENT_DOMAIN_MAP)
        return validOrigins.includes(origin)
    }, [])

    const cleanup = useCallback((closeWindow: boolean = true) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        
        if (listenerRef.current) {
            window.removeEventListener('message', listenerRef.current)
            listenerRef.current = null
        }
        
        if (windowRef.current && !windowRef.current.closed) {
            if (closeWindow) {
                windowRef.current.close()
            }
            windowRef.current = null
        }
    }, [])

    const handleMessage = useCallback((event: MessageEvent<ImpersonationResponse>) => {
        if (!isValidClientOrigin(event.origin)) {
            return
        }

        if (!event.data || typeof event.data !== 'object' || !event.data.type) {
            return
        }

        const { type, message } = event.data

        if (type === 'IMPERSONATE_SUCCESS') {
            cleanup(false)
        } else if (type === 'IMPERSONATE_ERROR') {
            dispatch(showAlert({
                message: message || 'Impersonation failed on client side',
                level: ALERT_LEVELS.ERROR
            }))
            cleanup()
        }
    }, [isValidClientOrigin, cleanup, dispatch])

    const impersonateNavigateUser = useCallback(async (userId: string, route?: string) => {
        if (!userId) {
            dispatch(showAlert({
                message: 'User ID is required',
                level: ALERT_LEVELS.ERROR
            }))
            return
        }

        setIsLoading(true)

        try {
            const clientDomain = getClientDomain()
            const { data: token } = await httpGetUserImpersonate(adminAccessToken, parseInt(userId, 10))
            
            if (!token) {
                throw new Error('No access token received from API')
            }

            const clientWindow = window.open(
                clientDomain,
                '_blank',
                'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no'
            )

            if (!clientWindow) {
                throw new Error('Failed to open client app window. Please check popup blocker settings.')
            }

            windowRef.current = clientWindow

            const waitForWindowLoad = () => {
                return new Promise<void>((resolve, reject) => {
                    let attempts = 0
                    const maxAttempts = 50
                    
                    const checkWindow = () => {
                        attempts++

                        if (clientWindow.closed) {
                            reject(new Error('Client window was closed before token could be sent'))
                            return
                        }

                        if (attempts >= maxAttempts) {
                            reject(new Error('Timeout waiting for client window to load'))
                            return
                        }

                        try {
                            if (clientWindow.location.href !== 'about:blank') {
                                resolve()
                                return
                            }
                        } catch (e) {
                            resolve()
                            return
                        }
                        
                        setTimeout(checkWindow, 1000)
                    }
                    
                    checkWindow()
                })
            }

            await waitForWindowLoad()

            listenerRef.current = handleMessage
            window.addEventListener('message', listenerRef.current)

            const message: ImpersonationMessage = {
                type: 'IMPERSONATE',
                token,
                adminToken: adminAccessToken,
                route
            }

            clientWindow.postMessage(message, clientDomain)

            timeoutRef.current = setTimeout(() => {
                dispatch(showAlert({
                    message: 'Timeout: Client app did not respond within 30 seconds',
                    level: ALERT_LEVELS.ERROR
                }))
                cleanup()
            }, 30000)

        } catch (error) {
            dispatch(showAlert({
                message: error instanceof Error ? error.message : 'Unknown error occurred',
                level: ALERT_LEVELS.ERROR
            }))
            cleanup()
        } finally {
            setIsLoading(false)
        }
    }, [adminAccessToken, getClientDomain, handleMessage, cleanup, dispatch])

    useEffect(() => {
        return () => {
            cleanup()
        }
    }, [cleanup])

    return {
        isLoading,
        impersonateNavigateUser
    }
}